// Stores
import { useSearchResultsStore } from '@white-label-store/search-results';

// Types
import type {
  ParkingFormData,
  LoungeFormData,
  TravelInsuranceFormData
} from '@white-label-types/component';
import type {
  SearchProducts,
  productTypeId,
} from '@white-label-types/parking-booking';

// Packages
import { v4 as uuidv4 } from 'uuid';
import { createSearchCriteria } from '@white-label-helper/data-parsers-create-search-criteria';
import { buildSearchResults, buildSearchResultsForTerminalFlights } from '@white-label-helper/data-parsers-search-results';
import {
  FEATURE_FLAGS,
} from '@white-label-configuration/constants';
import { getFeatureFlag } from '@white-label-plugin/launch-darkly';
import {
  getProductSearchResults,
  searchProductAvailability,
} from '@white-label-helper/api-parkings-product';
import { getAppVariable } from '@white-label-helper/get-app-variable';
import {
  addCustomMetric,
  trackAction,
} from '@white-label-helper/shared-helpers';
import { TerminalFlight } from '@white-label-types/terminal-flight';
import { datadogRum } from "@datadog/browser-rum";
// Mocks
import { mockedTravelInsuranceResponse } from './mock-data/mocked-return-insurance-response';

type AdditionalFormData = {
  parking?: ParkingFormData;
  lounges?: LoungeFormData;
  fasttrack?: LoungeFormData;
}

export async function fetchProductSearchResults(
  searchId: string,
  airportCode: string | undefined,
  productId: productTypeId
) {
  const store = useSearchResultsStore();
  const startTime = performance.now();
  const is_sync = getAppVariable('is_sync');
  return new Promise((resolve, reject) => {
    getProductSearchResults(searchId).then((response) => {
      addCustomMetric('tracking', 'Search Metric: Completed', {
        is_simple_search: String(is_sync),
        is_classic_search: String(!is_sync),
        event: 'SearchCompletedEvent',
        airport_code: airportCode,
      }, startTime, 'search_service', airportCode);

      store.updatePlaybooks(response.body?.data?.playbooks[productId]);
      if (response.body.data.search.inventory_metadata) {
        store.updateInventoryMetadata(response.body.data.search.inventory_metadata);
      }

      if (response.body.data.errors) {
        store.updateSearchResultsErrors(response.body.data.errors);
      }

      const results = response.body.data?.results;
      const productData = results?.[productId];
      const inventoryProvider = productData?.[0]?.inventory_provider;

      if (response.body.data.booking_fee) {
        store.updateBookingFee(response.body.data.booking_fee)
      }

      if (productId == 'fasttrack' && inventoryProvider === "copenhagen-optimization" && Array.isArray(productData)) {
        resolve({results: buildSearchResultsForTerminalFlights(productData), is_more_coming: response.body.data.is_more_coming});
      } else {
        // If multiple product type results exist, store them in search results store
        const multiProductTypeKeys = Object.keys(results);
        if (multiProductTypeKeys.length) {
          multiProductTypeKeys.forEach((key) => {
            store.updateSearchResultsByProductType({
              searchResults: buildSearchResults(response.body.data.results[key], key as productTypeId),
              productType: key as productTypeId,
            });
          });

          if (getFeatureFlag(FEATURE_FLAGS.EM_228_TRAVEL_INSURANCE)) {
            store.updateSearchResultsByProductType({
              searchResults: buildSearchResults(mockedTravelInsuranceResponse.insurance, 'travel_insurance' as productTypeId),
              productType: 'travel_insurance' as productTypeId,
            });
          }
        }
        resolve({results: buildSearchResults(productData, productId), is_more_coming: response.body.data.is_more_coming});
      }
  }).catch((error) => {
    console.error("Error fetching product search results:", error);
    reject(error);
  })});
}
export function searchPollingHelper() {
  const startTime = performance.now();
  const is_sync = getAppVariable('is_sync');
  let searchId: string;

  function pollProductSearchResults(
    searchId: string,
    airportCode: string | undefined,
    productId: productTypeId,
    initialInterval: number = 600
  ) {
    console.log('Starting pollProductSearchResults with searchId:', searchId);
    return new Promise((resolve, reject) => {
      let interval = initialInterval;
      let attempts = 0;
      function poll() {
        attempts++;
        if (attempts > 10) {
          reject(new Error('Polling timed out after 10 attempts'));
          return;
        }
        console.log('Polling attempt', attempts, 'for searchId:', searchId);
        fetchProductSearchResults(searchId, airportCode, productId)
          .then((resp) => {
            if (resp.is_more_coming === false) {
              resolve(resp.results);
            } else {
              interval = attempts > 1 ? Math.min(Math.floor(interval * 1.5), 8000) : initialInterval;
              setTimeout(poll, interval);
            }
          })
          .catch((error) => {
            if (!error.product_code || error.product_code === getAppVariable('primary_product_type')) {
              reject(new Error('SearchProductFailedEvent'));
            } else {
              addCustomMetric("error", "Search Metric: Secondary product error", {
                is_simple_search: String(is_sync),
                is_classic_search: String(!is_sync),
                failure_response: error.error?.response || "An error occurred",
              }, startTime, "search_service", airportCode);
              interval = Math.min(Math.floor(interval * 1.5), 8000);

              setTimeout(poll, interval);
            }
          });
      }
      poll();
    });
  }

  function searchProducts(
    formData: ParkingFormData | LoungeFormData | TravelInsuranceFormData,
    productId: productTypeId = 'parking',
    locale: string = '',
    additionalFormData?: AdditionalFormData,
  ) {
    const startTime = performance.now();
    const is_sync = getAppVariable('is_sync');
    const airportCode = formData.airport as string | undefined;

    return new Promise<SearchProducts[] | TerminalFlight[]>((resolve, reject) => {

      const store = useSearchResultsStore();
      searchId = uuidv4();
      store.updateDateTimeOfLastSearch(new Date());

      let language = getAppVariable('default_language');
      if (locale) {
        language = locale;
      } else {
        language = this.$i18n.locale;
      }

      const searchCriteria = createSearchCriteria(formData, searchId, productId, language);

      // If `additionalFormData` is defined, create search criteria for each additional product type
      if (additionalFormData !== undefined) {
        (Object.keys(additionalFormData) as (keyof AdditionalFormData)[]).forEach((key) => {
          if (additionalFormData[key] === undefined) return;
          searchCriteria.criteria[key] = createSearchCriteria(
            additionalFormData[key],
            searchId,
            key,
            language
          )['criteria'][key];
        });
      }


      store.updateSearchToken(searchId);
      console.log('store.updateSearchToken(searchId)', `${searchId}`)

      return searchProductAvailability(searchCriteria)
        .then((response) => {
          trackAction('searchProductAvailabilitySuccess', {
            searchCriteria: JSON.stringify(searchCriteria.criteria),
            searchId: searchCriteria.token,
          });
          if (getFeatureFlag('EM_1379_POLLING_LOGS', false)) {
            console.log(`searchProductAvailabilitySuccess, ${searchCriteria.token}`);
          }

          store.updatePlaybooks(response.body.data.playbooks[productId]);
          if (response.body.data.search.inventory_metadata) {
            store.updateInventoryMetadata(response.body.data.search.inventory_metadata);
          }

          if (response.body.data.errors) {
            store.updateSearchResultsErrors(response.body.data.errors);
          }

          if (response.body.data.booking_fee) {
            store.updateBookingFee(response.body.data.booking_fee)
          }

          if (!response.body.data.is_more_coming) {
            // If multiple product type results exist, store them in search results store
            const multiProductTypeKeys = Object.keys(response.body.data?.results);
            if (multiProductTypeKeys.length) {
              multiProductTypeKeys.forEach((key) => {
                store.updateSearchResultsByProductType({
                  searchResults: buildSearchResults(
                    response.body.data.results[key],
                    key as productTypeId
                  ),
                  productType: key as productTypeId,
                })
              });
              if (getFeatureFlag(FEATURE_FLAGS.EM_228_TRAVEL_INSURANCE)) {
                store.updateSearchResultsByProductType({
                  searchResults: buildSearchResults(mockedTravelInsuranceResponse.insurance, 'travel_insurance' as productTypeId),
                  productType: 'travel_insurance' as productTypeId,
                });
              }
            }
            resolve(
              buildSearchResults(
                response.body.data.results[productId],
                productId
              )
            );
          } else {
            if (getFeatureFlag('EM_1379_POLLING_LOGS', false)) {
              console.log('pollProductSearchResults', searchId);
            }
            pollProductSearchResults(searchId, airportCode, productId)
              .then(resolve)
              .catch(reject);
          }
        })
        .catch((error: { message: string }) => {
          datadogRum.addError(
            new Error('searchProductAvailability failed', { cause: error })
          );

          let data = null;
          try {
            data = JSON.parse(error.message);
          } catch (e) {
            console.log('possible error: ', e);
            datadogRum.addError('searchProductAvailability failed', {
              'search_service': {
                ...data,
              }
            });
          }
          reject(error);
        });
    })
    .catch((e) => {
      addCustomMetric("error", "Search Metric: Exception", {
        is_simple_search: String(is_sync),
        is_classic_search: String(!is_sync),
        failure_response: e.message,
        search_id: searchId,
      }, startTime, "search_service", airportCode);

      throw e;
    });
  }

  return {
    searchProducts
  };
}
